import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { nomenclature, type Nomenclature, type NomenclatureCreate } from '@/api/catalogs';
import { refs } from '@/api/refs';
import Drawer from '@/components/Drawer';
import ImportedBadge from '@/shared/components/ImportedBadge';
import AccountSelect from '@/components/AccountSelect';
import { NomenclaturePricesTab } from './NomenclaturePricesTab';
import { NomenclatureKitTab } from './NomenclatureKitTab';
import { NomenclatureSeriesTab } from './NomenclatureSeriesTab';
import { NomenclatureAccountPreview } from './NomenclatureAccountPreview';
import UktzedPicker from '@/components/UktzedPicker';
import DkppPicker from '@/components/DkppPicker';
import { isDirty } from '@/utils/dirty';
import { useConfirm } from '@/components/ConfirmProvider';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';

const EMPTY: NomenclatureCreate = {
  code: null,
  article: null,
  name: '',
  full_name: null,
  is_service: false,
  is_excise: false,
  ukt_zed_code: null,
  country_origin_code: null,
  base_uom_id: null,
  vat_rate_id: null,
  default_cost_item_id: null,
  kind_id: null,
  account_id: null,
  transfer_account_id: null,
  off_balance_account_id: null,
  off_balance_transfer_account_id: null,
  tax_purpose_id: null,
  income_cost_tax_purpose_id: null,
};

export default function NomenclaturePage() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');  // '' = всі види
  const [editing, setEditing] = useState<Nomenclature | 'new' | null>(null);
  const [form, setForm] = useState<NomenclatureCreate>(EMPTY);
  const [tab, setTab] = useState<'main' | 'accounts' | 'prices' | 'kit' | 'series'>('main');

  // TD-PAGINATION (2026-06-07): серверна пагінація через ag-Grid Infinite.
  const gridApiRef = useRef<GridApi<Nomenclature> | null>(null);
  // Збереження ширини колонок між перезавантаженнями сторінки.
  const colW = useGridColumnWidths('nomenclature');
  const { datasource, total } = useInfiniteGridDatasource<Nomenclature>(
    (limit, offset, sort) => nomenclature.list({
      q: search || undefined, kind_id: kindFilter || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [search, kindFilter],
  );
  const refreshGrid = () => gridApiRef.current?.refreshInfiniteCache();
  // 2026-06-02: UOM classifier (для базової одиниці виміру).
  const uomsList = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });
  // Бух-реквізити: ставка ПДВ + стаття затрат (1С УТП).
  const vatRatesList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const costItemsList = useQuery({ queryKey: ['cost-items'], queryFn: refs.costItems });
  // Види номенклатури (tenant-scoped). 2026-06-06.
  const kindsList = useQuery({ queryKey: ['nomenclature-kinds'], queryFn: refs.nomenclatureKinds.list });
  const taxPurposesQ = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes });
  const incomeCostQ = useQuery({ queryKey: ['income-cost-tax-purposes'], queryFn: refs.incomeCostTaxPurposes });
  const kindById = useMemo(
    () => new Map((kindsList.data ?? []).map((k) => [k.id, k])),
    [kindsList.data],
  );
  const create = useMutation({
    mutationFn: (p: NomenclatureCreate) => nomenclature.create(p),
    onSuccess: () => { refreshGrid(); setEditing(null); },
  });
  const update = useMutation({
    mutationFn: ({ id, p }: { id: string; p: Partial<NomenclatureCreate> }) => nomenclature.update(id, p),
    onSuccess: () => { refreshGrid(); setEditing(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => nomenclature.remove(id),
    onSuccess: () => refreshGrid(),
  });

  const cols = useMemo<ColDef<Nomenclature>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      // Значок «І» імпорту з 1С.
      cellRenderer: (p: { data?: Nomenclature }) => <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'code', headerName: t('nom.code'), width: 110, sortable: true },
    { field: 'article', headerName: t('nom.article'), width: 110, sortable: true },
    { field: 'name', headerName: t('nom.name'), flex: 1, minWidth: 280, sortable: true },
    {
      // Вид — valueGetter (резолв kind_id→name), серверна сортировка по UUID
      // немає сенсу. sortable:false.
      headerName: 'Вид',
      width: 140, sortable: false,
      valueGetter: (p) => (p.data?.kind_id ? kindById.get(p.data.kind_id)?.name ?? '' : ''),
    },
    {
      field: 'is_service',
      headerName: 'Тип',
      width: 100, sortable: false,
      valueFormatter: (p) => (p.value ? 'Послуга' : 'Товар'),
    },
    { field: 'ukt_zed_code', headerName: t('nom.ukt_zed'), width: 130, sortable: true },
  ], [t, kindById]);

  function startCreate() { setForm(EMPTY); setTab('main'); setEditing('new'); }
  function startEdit(n: Nomenclature) {
    setTab('main');
    setForm({
      code: n.code, article: n.article, name: n.name, full_name: n.full_name,
      is_service: n.is_service, is_excise: n.is_excise,
      use_series: !!n.use_series,
      ukt_zed_code: n.ukt_zed_code, country_origin_code: n.country_origin_code,
      base_uom_id: n.base_uom_id,
      vat_rate_id: n.vat_rate_id, default_cost_item_id: n.default_cost_item_id,
      kind_id: n.kind_id,
      account_id: n.account_id, transfer_account_id: n.transfer_account_id,
      off_balance_account_id: n.off_balance_account_id,
      off_balance_transfer_account_id: n.off_balance_transfer_account_id,
      tax_purpose_id: n.tax_purpose_id,
      income_cost_tax_purpose_id: n.income_cost_tax_purpose_id,
    });
    setEditing(n);
  }
  function save() {
    if (editing === 'new') create.mutate(form);
    else if (editing) update.mutate({ id: editing.id, p: form });
  }

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {t('nom.title')}
          {total !== null && (
            <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
              ({total})
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* Фільтр по виду номенклатури (2026-06-06). */}
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <option value="">Усі види</option>
            {(kindsList.data ?? []).map((k) => (
              <option key={k.id} value={k.id}>{k.name}</option>
            ))}
          </select>
          {/* «Керувати видами →» прибрано: види тепер окремий пункт меню
              «Товари | Послуги → Види номенклатури» (рішення власника 2026-06-15). */}
          <button className="primary" onClick={startCreate}>+ {t('nom.new')}</button>
        </div>
      </div>
      <div className="ag-theme-quartz grid">
        <AgGridReact<Nomenclature>
          rowModelType="infinite"
          datasource={datasource}
          cacheBlockSize={100}
          columnDefs={cols}
          onGridReady={(e: GridReadyEvent<Nomenclature>) => { gridApiRef.current = e.api; colW.applyTo(e.api); }}
          onColumnResized={colW.onColumnResized}
          onRowDoubleClicked={(e) => e.data && startEdit(e.data)}
          rowSelection="single"
          getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
        />
      </div>

      <Drawer
        open={editing !== null}
        width="wide"
        title={editing === 'new' ? t('nom.new') : (editing as Nomenclature | null)?.name ?? ''}
        onClose={() => setEditing(null)}
        dirty={editing !== null && isDirty(form, editing === 'new' ? null : editing as unknown as Partial<NomenclatureCreate>)}
        footer={
          <>
            {editing !== 'new' && editing && (
              <button
                className="danger"
                onClick={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) { remove.mutate(editing.id); setEditing(null); } }) }}
              >
                {t('common.delete')}
              </button>
            )}
            <button onClick={() => setEditing(null)}>{t('common.cancel')}</button>
            <button className="primary" onClick={save} disabled={create.isPending || update.isPending}>
              {t('common.save')}
            </button>
          </>
        }
      >
        {/* Закладки: Основне | Ціни (Ціни — лише для збереженої номенклатури). */}
        <div className="seg-tabs">
          <button className={tab === 'main' ? 'active' : ''} onClick={() => setTab('main')}>
            Основне
          </button>
          <button className={tab === 'accounts' ? 'active' : ''} onClick={() => setTab('accounts')}>
            Рахунки та податки
          </button>
          <button className={tab === 'prices' ? 'active' : ''}
            disabled={editing === 'new'}
            title={editing === 'new' ? 'Спершу збережіть номенклатуру' : undefined}
            onClick={() => setTab('prices')}>
            Ціни
          </button>
          <button className={tab === 'kit' ? 'active' : ''}
            disabled={editing === 'new'}
            title={editing === 'new' ? 'Спершу збережіть номенклатуру' : undefined}
            onClick={() => setTab('kit')}>
            Комплектація
          </button>
          <button className={tab === 'series' ? 'active' : ''}
            disabled={editing === 'new'}
            title={editing === 'new' ? 'Спершу збережіть номенклатуру' : undefined}
            onClick={() => setTab('series')}>
            Серії/Характеристики
          </button>
        </div>

        {tab === 'prices' && editing !== 'new' && editing && (
          <NomenclaturePricesTab nomenclatureId={(editing as Nomenclature).id} />
        )}

        {tab === 'kit' && editing !== 'new' && editing && (
          <NomenclatureKitTab nomenclatureId={(editing as Nomenclature).id} />
        )}

        {tab === 'series' && editing !== 'new' && editing && (
          <NomenclatureSeriesTab nomenclatureId={(editing as Nomenclature).id} />
        )}

        {tab === 'accounts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* Read-only показ ефективного рахунку з матриці (1С-міграція кладе
                рахунки сюди, не у плоске поле картки). Лише для збереженої
                номенклатури (потрібен її id). */}
            {editing !== 'new' && editing && (
              <NomenclatureAccountPreview nomenclatureId={editing.id} />
            )}
            <p style={{ color: 'var(--muted)', margin: 0, fontSize: '0.85em' }}>
              Поля нижче — <b>override картки</b> для простих випадків. Спрацюють
              лише якщо для юрособи немає точнішого правила (за видом / складом).
              Правила — Довідники → Рахунки обліку (правила).
            </p>
            <label>Рахунок обліку
              <AccountSelect value={form.account_id ?? ''}
                onChange={(id) => setForm({ ...form, account_id: id || null })}
                placeholder="— стандартний (281 тощо) —" />
            </label>
            <label>Рахунок передачі
              <AccountSelect value={form.transfer_account_id ?? ''}
                onChange={(id) => setForm({ ...form, transfer_account_id: id || null })}
                placeholder="— не задано —" />
            </label>
            <label>Забалансовий рахунок обліку
              <AccountSelect value={form.off_balance_account_id ?? ''} offBalance
                onChange={(id) => setForm({ ...form, off_balance_account_id: id || null })}
                placeholder="— не задано —" />
            </label>
            <label>Забалансовий рахунок передачі
              <AccountSelect value={form.off_balance_transfer_account_id ?? ''} offBalance
                onChange={(id) => setForm({ ...form, off_balance_transfer_account_id: id || null })}
                placeholder="— не задано —" />
            </label>
            <label>Податкове призначення (ПДВ)
              <select value={form.tax_purpose_id ?? ''}
                onChange={(e) => setForm({ ...form, tax_purpose_id: e.target.value || null })}>
                <option value="">— не задано —</option>
                {(taxPurposesQ.data ?? []).map((tp) => (
                  <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
                ))}
              </select>
            </label>
            <label>Податкове призначення доходів/затрат
              <select value={form.income_cost_tax_purpose_id ?? ''}
                onChange={(e) => setForm({ ...form, income_cost_tax_purpose_id: e.target.value || null })}>
                <option value="">— не задано —</option>
                {(incomeCostQ.data ?? []).map((ic) => (
                  <option key={ic.id} value={ic.id}>{ic.name_uk}</option>
                ))}
              </select>
            </label>
          </div>
        )}

        {tab === 'main' && (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="field">
            <label>
              {t('nom.code')}{' '}
              <small style={{ color: 'var(--muted)' }}>
                (auto: NOM-1, якщо порожнє)
              </small>
            </label>
            <input
              value={form.code ?? ''}
              onChange={(e) => setForm({ ...form, code: e.target.value || null })}
              placeholder="NOM-1 (auto)"
            />
          </div>
          <div className="field">
            <label>{t('nom.article')}</label>
            <input value={form.article ?? ''} onChange={(e) => setForm({ ...form, article: e.target.value || null })} />
          </div>
        </div>
        <div className="field">
          <label>{t('nom.name')}</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="field">
          <label>{t('nom.full_name')}</label>
          <input value={form.full_name ?? ''} onChange={(e) => setForm({ ...form, full_name: e.target.value || null })} />
        </div>
        {/* Вид номенклатури (2026-06-06): обрав вид → тип (is_service)
            визначається автоматично з виду. */}
        <div className="field">
          <label>Вид номенклатури</label>
          <select
            value={form.kind_id ?? ''}
            onChange={(e) => {
              const kid = e.target.value || null;
              const kind = kid ? kindById.get(kid) : null;
              setForm({
                ...form,
                kind_id: kid,
                // Тип авто з виду (юзер не редагує тип напряму).
                is_service: kind ? kind.is_service : form.is_service,
              });
            }}
          >
            <option value="">— не задано —</option>
            {(kindsList.data ?? []).map((k) => (
              <option key={k.id} value={k.id}>{k.name}</option>
            ))}
          </select>
          <small style={{ color: 'var(--muted)' }}>
            Тип: <strong>{form.is_service ? 'Послуга' : 'Товар'}</strong>
            {' '}(визначається з виду)
          </small>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <label><input type="checkbox" checked={form.is_excise} onChange={(e) => setForm({ ...form, is_excise: e.target.checked })} style={{ width: 'auto', marginRight: '0.4rem' }} />{t('nom.is_excise')}</label>
          <label title="Учёт серий: при отгрузке обязательно указывать серию"><input type="checkbox" checked={!!form.use_series} onChange={(e) => setForm({ ...form, use_series: e.target.checked })} style={{ width: 'auto', marginRight: '0.4rem' }} />Облік серій</label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
          <div className="field">
            <label>Одиниця виміру</label>
            <select
              value={form.base_uom_id ?? ''}
              onChange={(e) => setForm({ ...form, base_uom_id: e.target.value || null })}
            >
              <option value="">— Виберіть —</option>
              {uomsList.data?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.short_name} — {u.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t('nom.country')}</label>
            <input value={form.country_origin_code ?? ''} onChange={(e) => setForm({ ...form, country_origin_code: e.target.value || null })} maxLength={3} />
          </div>
        </div>
        {/* Бух-реквізити: ставка ПДВ + стаття затрат (1С УТП). */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
          <div className="field">
            <label>Ставка ПДВ</label>
            <select
              value={form.vat_rate_id ?? ''}
              onChange={(e) => setForm({ ...form, vat_rate_id: e.target.value || null })}
            >
              <option value="">— Не задано —</option>
              {vatRatesList.data?.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Стаття затрат</label>
            <select
              value={form.default_cost_item_id ?? ''}
              onChange={(e) => setForm({ ...form, default_cost_item_id: e.target.value || null })}
            >
              <option value="">— Не задано —</option>
              {costItemsList.data?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <div className="field">
            {/* «Код для ПН»: одне поле ukt_zed_code, але джерело різне —
                товар → УКТЗЕД (picker з пошуком по довіднику),
                послуга → ДКПП (текстовий ввід; довідника ДКПП ще немає,
                TD-NOM-3). Так фронт не плутає коди послуг з УКТЗЕД-пошуком. */}
            {form.is_service ? (
              <>
                <label>Код ДКПП (послуги)</label>
                <DkppPicker
                  value={form.ukt_zed_code ?? null}
                  onChange={(code) => setForm({ ...form, ukt_zed_code: code })}
                />
              </>
            ) : (
              <>
                <label>{t('nom.ukt_zed')}</label>
                <UktzedPicker
                  value={form.ukt_zed_code ?? null}
                  onChange={(code) => setForm({ ...form, ukt_zed_code: code })}
                />
              </>
            )}
          </div>
        </div>
        </>
        )}
      </Drawer>
    </>
  );
}
